import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, GraduationCap, Briefcase, CheckCircle2, TrendingUp, Recycle, Award, UserPlus, X, Loader2, Heart, RefreshCw, Handshake, Calendar, MapPin, Clock } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Layout } from '../components/Layout';
import { SEO, pageSEO } from '../components/SEO';
import { saveSubmission } from '../services/databaseService';
import { sendViaWeb3Forms } from '../services/web3formsService';
type ModalType = 'membre' | 'recruter' | null;
export function HomePage() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(formRef.current!);
    let submission: any = {};
    if (activeModal === 'membre') {
      submission = {
        type: 'membre' as const,
        nom: formData.get('nom') as string || '',
        prenom: formData.get('prenom') as string || '',
        telephone: formData.get('telephone') as string || '',
        email: formData.get('email') as string || '',
        ville: formData.get('ville') as string || '',
        profession: formData.get('profession') as string || '',
        typeMembre: formData.get('typeMembre') as string || '',
        contribution: formData.get('contribution') as string || '',
        motivations: formData.get('motivations') as string || ''
      };
    } else if (activeModal === 'recruter') {
      submission = {
        type: 'recruter' as const,
        nom: formData.get('nom') as string || '',
        prenom: formData.get('prenom') as string || '',
        telephone: formData.get('telephone') as string || '',
        email: formData.get('email') as string || '',
        ville: formData.get('ville') as string || '',
        quartier: formData.get('quartier') as string || '',
        typePersonnel: formData.get('typePersonnel') as string || '',
        typeContrat: formData.get('typeContrat') as string || '',
        dateDebut: formData.get('dateDebut') as string || '',
        description: formData.get('description') as string || ''
      };
    }
    setTimeout(async () => {
      try {
        saveSubmission(submission);
        // Envoyer via Web3Forms
        const formData2 = new FormData(formRef.current!);
        const fields: Record<string, string> = {};
        formData2.forEach((value, key) => {
          if (typeof value === 'string') {
            fields[key] = value;
          }
        });
        const web3Result = await sendViaWeb3Forms({
          type: activeModal!,
          fields
        });
        console.log('📧 Web3Forms result:', web3Result);
        setIsSubmitting(false);
        setIsSuccess(true);
      } catch (error) {
        console.error('Error saving submission:', error);
        setIsSubmitting(false);
        alert('Une erreur est survenue. Veuillez réessayer.');
      }
    }, 500);
  };
  const closeModal = () => {
    setActiveModal(null);
    setIsSuccess(false);
  };
  const inputClass = 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none transition-all';
  const selectClass = 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none transition-all bg-white';
  const labelClass = 'text-sm font-medium text-gray-700';
  const modalConfigs = {
    membre: {
      title: "Devenir Membre de l'ADCC",
      subtitle: "Rejoignez notre communauté engagée pour l'insertion professionnelle digne",
      successMessage: "Votre demande d'adhésion a été enregistrée. Notre équipe vous contactera prochainement pour finaliser votre inscription.",
      icon: UserPlus
    },
    recruter: {
      title: 'Recruter un Personnel Formé',
      subtitle: 'Décrivez vos besoins et nous vous mettrons en relation avec des profils qualifiés',
      successMessage: 'Demande reçue ! Nous vous proposerons des profils correspondant à vos critères.',
      icon: Briefcase
    }
  };
  return <Layout data-id="element-1328">
      <SEO {...pageSEO.home} data-id="element-1329" />

      {/* Hero Section */}
      <section className="relative bg-[#2C3E50] text-white py-20 md:py-32 overflow-hidden" data-id="element-1330">
        <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/jfY9WFQQPLNmCKBmbcpuEW/Femmes_Menage_Emploi_3.png')] bg-cover bg-center opacity-30" data-id="element-1331"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1332">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1333">
            {/* Main Content */}
            <div className="lg:col-span-2" data-id="element-1334">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-1335">
                Développer. Valoriser.{' '}
                <span className="text-[#1E88E5]" data-id="element-1336">Insérer.</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-6 leading-relaxed" data-id="element-1337">
                L'Association Dignité, Compétences Création (ADCC) agit au Bénin
                pour l'accompagnement à la professionalisation et l'insertion de
                personnels de maison et de services, avec pour objectif un accès
                durable à un emploi digne.
              </p>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6 mb-8" data-id="element-1338">
                <p className="text-lg text-gray-100 mb-4" data-id="element-1339">
                  <strong data-id="element-1340">
                    Chez ADCC, l'accompagnement pré et post insertion.
                  </strong>
                </p>
                <p className="text-base text-gray-200" data-id="element-1341">
                  Nous accompagnons les bénéficiaires jusqu'à l'insertion
                  professionnelle, en facilitant leur mise en relation avec des
                  employeurs, des familles, des structures et des partenaires,
                  dans un cadre éthique, transparent et respectueux des droits
                  humains.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-1342">
                <Link to="/actions" data-id="element-1343">
                  <Button size="lg" className="w-full sm:w-auto gap-2" data-id="element-1344">
                    Découvrir nos actions
                    <ArrowRight className="w-5 h-5" data-id="element-1345" />
                  </Button>
                </Link>
                <Link to="/about" data-id="element-1346">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto border-white text-white hover:bg-white/10 hover:text-white" data-id="element-1347">
                    Notre approche
                  </Button>
                </Link>
              </div>
            </div>

            {/* CTA Cards - Right Side */}
            <div className="space-y-4" data-id="element-1348">
              {/* Devenir Membre Card */}
              <div className="bg-white rounded-xl p-6 shadow-xl" data-id="element-1349">
                <div className="flex items-center gap-3 mb-4" data-id="element-1350">
                  <div className="p-3 bg-[#1E88E5] rounded-full" data-id="element-1351">
                    <UserPlus className="w-6 h-6 text-white" data-id="element-1352" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-1353">
                    Rejoignez-nous
                  </h3>
                </div>
                <p className="text-gray-600 mb-4 text-sm" data-id="element-1354">
                  Devenez bénévole de l'ADCC et participez à notre mission pour
                  l'insertion professionnelle digne.
                </p>
                <Link to="/benevoles" data-id="element-1355">
                  <Button className="w-full gap-2" data-id="element-1356">
                    <Heart className="w-4 h-4" data-id="element-1357" />
                    Devenir bénévole
                  </Button>
                </Link>
              </div>

              {/* Recruter Card */}
              <div className="bg-gradient-to-br from-[#1E88E5] to-[#1565C0] rounded-xl p-6 shadow-xl text-white" data-id="element-1358">
                <div className="flex items-center gap-3 mb-4" data-id="element-1359">
                  <div className="p-3 bg-white/20 rounded-full" data-id="element-1360">
                    <Briefcase className="w-6 h-6 text-white" data-id="element-1361" />
                  </div>
                  <h3 className="text-xl font-bold" data-id="element-1362">Vous recrutez ?</h3>
                </div>
                <p className="text-blue-100 mb-4 text-sm" data-id="element-1363">
                  Accédez à des personnels compétents et accompagnés pour vos
                  besoins.
                </p>
                <Button variant="ghost" className="w-full bg-white text-[#1E88E5] hover:bg-gray-100 hover:text-[#1E88E5] gap-2" onClick={() => setActiveModal('recruter')} data-id="element-1364">
                  <Users className="w-4 h-4" data-id="element-1365" />
                  Recruter un personnel
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Stats Section - Updated with real numbers */}
      <section className="py-12 bg-white -mt-8 relative z-10" data-id="element-1366">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1367">
          <div className="text-center mb-8" data-id="element-1368">
            <span className="inline-block bg-amber-100 text-amber-800 px-4 py-1 rounded-full text-sm font-medium" data-id="element-1369">
              Nos objectifs 2026
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1370">
            {[{
            icon: GraduationCap,
            label: 'Personnes à accompagner',
            value: '100',
            subtitle: 'Objectif 2026'
          }, {
            icon: Briefcase,
            label: 'Taux de placement visé',
            value: '60%',
            subtitle: 'Des bénéficiaires accompagnés'
          }, {
            icon: Heart,
            label: "D'insertion éthique",
            value: '100%',
            subtitle: 'Transparents et dignes'
          }].map((stat, index) => <Card key={index} className="bg-white shadow-lg border-t-4 border-t-[#1E88E5]" data-id="element-1371">
                <CardContent className="flex items-center p-6" data-id="element-1372">
                  <div className="p-4 bg-blue-50 rounded-full mr-4" data-id="element-1373">
                    <stat.icon className="w-8 h-8 text-[#1E88E5]" data-id="element-1374" />
                  </div>
                  <div data-id="element-1375">
                    <p className="text-3xl font-bold text-gray-900" data-id="element-1376">
                      {stat.value}
                    </p>
                    <p className="text-gray-600 font-medium" data-id="element-1377">{stat.label}</p>
                    <p className="text-xs text-gray-400" data-id="element-1378">{stat.subtitle}</p>
                  </div>
                </CardContent>
              </Card>)}
          </div>
          <div className="text-center mt-6" data-id="element-1379">
            <p className="text-sm text-gray-500" data-id="element-1380">
              🌱 Notre premier forum a réuni ~10 personnels de maison pour
              cartographier les besoins
            </p>
          </div>
        </div>
      </section>

      {/* Value Chain Section - Updated with 5 pillars */}
      <section className="py-20 bg-gray-50" data-id="element-1381">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1382">
          <div className="text-center mb-12" data-id="element-1383">
            <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1384">
              De la montée en compétences à l'emploi : un parcours complet
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4" data-id="element-1385">
              Notre chaîne de valeur
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1386">
              L'ADCC développe une approche complète pour garantir un impact
              réel, mesurable et durable.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-12" data-id="element-1387">
            {[{
            step: '01',
            title: 'Développer',
            desc: 'Compétences techniques et humaines',
            icon: GraduationCap,
            color: 'bg-blue-50'
          }, {
            step: '02',
            title: 'Tester',
            desc: 'Validation et professionnalisation',
            icon: Award,
            color: 'bg-green-50'
          }, {
            step: '03',
            title: 'Insérer',
            desc: 'Mise en relation avec employeurs',
            icon: Briefcase,
            color: 'bg-purple-50'
          }, {
            step: '04',
            title: 'Suivre',
            desc: 'Accompagnement post-placement',
            icon: TrendingUp,
            color: 'bg-amber-50'
          }, {
            step: '05',
            title: 'Recycler',
            desc: 'Mise à jour des compétences',
            icon: Recycle,
            color: 'bg-teal-50'
          }].map((item, index) => <div key={index} className="relative" data-id="element-1388">
                <Card className="h-full bg-white hover:shadow-lg transition-shadow" data-id="element-1389">
                  <CardContent className="p-5" data-id="element-1390">
                    <div className="flex items-center justify-between mb-3" data-id="element-1391">
                      <span className="text-3xl font-bold text-gray-200" data-id="element-1392">
                        {item.step}
                      </span>
                      <div className={`p-2 ${item.color} rounded-lg`} data-id="element-1393">
                        <item.icon className="w-5 h-5 text-[#1E88E5]" data-id="element-1394" />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-1395">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-xs" data-id="element-1396">{item.desc}</p>
                  </CardContent>
                </Card>
                {index < 4 && <div className="hidden lg:block absolute top-1/2 -right-2 transform -translate-y-1/2 z-10" data-id="element-1397">
                    <ArrowRight className="w-4 h-4 text-[#1E88E5]" data-id="element-1398" />
                  </div>}
              </div>)}
          </div>

          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100" data-id="element-1399">
            <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1400">
              Notre engagement éthique
            </h3>
            <p className="text-lg text-gray-600 mb-6" data-id="element-1401">
              Nous œuvrons pour une insertion responsable, fondée sur :
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1402">
              {['La dignité du travail', 'La transparence des conditions', 'Le respect des personnes', 'La valorisation des compétences acquises'].map((item, index) => <div key={index} className="flex items-center gap-3" data-id="element-1403">
                  <CheckCircle2 className="w-5 h-5 text-[#1E88E5] flex-shrink-0" data-id="element-1404" />
                  <span className="text-gray-700" data-id="element-1405">{item}</span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section - Fixed overlap issue */}
      <section className="py-20 bg-white" data-id="element-1406">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1407">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1408">
            <div className="relative" data-id="element-1409">
              <div className="bg-gradient-to-br from-[#1E88E5]/10 via-[#2C3E50]/5 to-[#1E88E5]/10 rounded-2xl p-8 flex items-center justify-center min-h-[400px]" data-id="element-1410">
                <div className="text-center" data-id="element-1411">
                  <img src="https://cdn.magicpatterns.com/uploads/igFPFWe6QurLv1Fq8d9zkK/logo_sans_texte.png" alt="ADCC - Accompagner la croissance" className="w-48 h-48 mx-auto mb-6" data-id="element-1412" />
                  <div className="space-y-4" data-id="element-1413">
                    <div className="flex items-center justify-center gap-3 bg-white rounded-lg p-3 shadow-sm" data-id="element-1414">
                      <GraduationCap className="w-6 h-6 text-[#1E88E5]" data-id="element-1415" />
                      <span className="font-medium text-gray-700" data-id="element-1416">
                        Accompagnement à la professionalisation
                      </span>
                    </div>
                    <div className="flex items-center justify-center gap-3 bg-white rounded-lg p-3 shadow-sm" data-id="element-1417">
                      <Briefcase className="w-6 h-6 text-[#1E88E5]" data-id="element-1418" />
                      <span className="font-medium text-gray-700" data-id="element-1419">
                        Insertion accompagnée
                      </span>
                    </div>
                    <div className="flex items-center justify-center gap-3 bg-white rounded-lg p-3 shadow-sm" data-id="element-1420">
                      <RefreshCw className="w-6 h-6 text-[#1E88E5]" data-id="element-1421" />
                      <span className="font-medium text-gray-700" data-id="element-1422">
                        Recyclage des compétences
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 bg-[#1E88E5] text-white p-6 rounded-lg shadow-lg" data-id="element-1423">
                <p className="font-bold text-lg mb-2" data-id="element-1424">
                  "Des compétences avérées, des emplois dignes."
                </p>
                <p className="text-sm text-blue-100" data-id="element-1425">
                  L'impact se mesure aussi à l'accès à l'emploi.
                </p>
              </div>
            </div>
            <div data-id="element-1426">
              <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1427">
                Notre Impact
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-6" data-id="element-1428">
                Renforcer des compétences ne suffit pas. Insérer durablement est
                notre priorité.
              </h2>
              <p className="text-gray-600 text-lg mb-6 leading-relaxed" data-id="element-1429">
                L'ADCC s'engage à promouvoir la dignité humaine par le
                renforcement des compétences et leur transformation en
                opportunités d'emploi concrètes. Nous croyons que chaque
                individu possède un potentiel unique qui ne demande qu'à être
                révélé et valorisé sur le marché du travail.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-1430">
                {['Développement de compétences métier', 'Insertion en emploi accompagné', 'Suivi post-insertion et médiation', 'Recyclage des personnels déjà en service', 'Développement de partenariats employeurs'].map((item, index) => <li key={index} className="flex items-start" data-id="element-1431">
                    <CheckCircle2 className="w-6 h-6 text-[#1E88E5] mr-3 flex-shrink-0 mt-0.5" data-id="element-1432" />
                    <span className="text-gray-700" data-id="element-1433">{item}</span>
                  </li>)}
              </ul>
              <Link to="/about" data-id="element-1434">
                <Button variant="secondary" data-id="element-1435">
                  Découvrir notre approche complète
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-16 bg-gray-50" data-id="element-1436">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1437">
          <div className="text-center mb-12" data-id="element-1438">
            <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1439">
              Ils nous font confiance
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4" data-id="element-1440">
              Nos Partenaires
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-1441">
              L'ADCC s'associe à des acteurs engagés pour maximiser l'impact de
              ses actions en faveur de l'insertion professionnelle digne.
            </p>
          </div>

          {/* Premium Partner */}
          <div className="max-w-2xl mx-auto" data-id="element-1442">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 relative overflow-hidden" data-id="element-1443">
              {/* Premium badge */}
              <div className="absolute top-4 right-4" data-id="element-1444">
                <span className="inline-flex items-center gap-1.5 bg-gradient-to-r from-amber-400 to-amber-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-sm" data-id="element-1445">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-1446">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-1447" />
                  </svg>
                  Partenaire Premium
                </span>
              </div>

              <div className="flex flex-col md:flex-row items-center gap-8" data-id="element-1448">
                <div className="flex-shrink-0" data-id="element-1449">
                  <img src="https://cdn.magicpatterns.com/uploads/c5DXfmbRapYgHk79paP5RZ/Logo_Trace_Academia_(2).png" alt="TRACE ACADEMIA - Partenaire Premium" className="h-24 w-auto" data-id="element-1450" />
                </div>
                <div className="text-center md:text-left" data-id="element-1451">
                  <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1452">
                    TRACE ACADEMIA
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-1453">
                    Plateforme de formation en ligne leader en Afrique, TRACE
                    ACADEMIA rejoint l'ADCC pour renforcer les compétences des
                    personnels de maison à travers des contenus éducatifs de
                    qualité.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center md:justify-start" data-id="element-1454">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1455">
                      Formation digitale
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1456">
                      Certification
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-1457">
                      Contenus exclusifs
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Future partners placeholder */}
          <div className="mt-12 text-center" data-id="element-1458">
            <p className="text-gray-500 text-sm mb-4" data-id="element-1459">
              Vous souhaitez devenir partenaire de l'ADCC ?
            </p>
            <Link to="/partner" data-id="element-1460">
              <Button variant="secondary" className="gap-2" data-id="element-1461">
                <Handshake className="w-4 h-4" data-id="element-1462" />
                Proposer un partenariat
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1E88E5] text-white" data-id="element-1463">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1464">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1465">
            Soutenez l'insertion professionnelle durable
          </h2>
          <p className="text-xl text-blue-100 mb-10" data-id="element-1466">
            Votre soutien permet non seulement de développer les compétences,
            mais aussi de financer des dispositifs d'accompagnement à
            l'insertion des bénéficiaires vers un emploi durable, et de
            structurer ainsi un modèle d'aide à l'emploi éthique et responsable.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1467">
            <Link to="/donate" data-id="element-1468">
              <Button variant="ghost" size="lg" className="bg-white text-[#1E88E5] hover:bg-gray-100 hover:text-[#1E88E5] font-medium gap-2 w-full sm:w-auto" data-id="element-1469">
                <Heart className="w-5 h-5" data-id="element-1470" />
                Faire un don
              </Button>
            </Link>
            <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 hover:text-white w-full sm:w-auto gap-2" onClick={() => setActiveModal('recruter')} data-id="element-1471">
              <Briefcase className="w-5 h-5" data-id="element-1472" />
              Recruter un personnel
            </Button>
          </div>
        </div>
      </section>

      {/* Modal */}
      {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto" data-id="element-1473">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl my-8 relative max-h-[90vh] overflow-y-auto" data-id="element-1474">
            <button onClick={closeModal} className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors z-10" data-id="element-1475">
              <X className="w-6 h-6" data-id="element-1476" />
            </button>

            <div className="p-8" data-id="element-1477">
              <div className="text-center mb-8" data-id="element-1478">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1E88E5] rounded-full mb-4" data-id="element-1479">
                  {activeModal === 'membre' ? <UserPlus className="w-8 h-8 text-white" data-id="element-1480" /> : <Briefcase className="w-8 h-8 text-white" data-id="element-1481" />}
                </div>
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-1482">
                  {modalConfigs[activeModal].title}
                </h2>
                <p className="text-gray-600 mt-2" data-id="element-1483">
                  {modalConfigs[activeModal].subtitle}
                </p>
              </div>

              {isSuccess ? <div className="text-center py-12" data-id="element-1484">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1485">
                    <CheckCircle2 className="w-10 h-10 text-green-600" data-id="element-1486" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1487">
                    {activeModal === 'membre' ? 'Bienvenue dans la famille ADCC !' : 'Demande envoyée !'}
                  </h3>
                  <p className="text-gray-600 mb-8" data-id="element-1488">
                    {modalConfigs[activeModal].successMessage}
                  </p>
                  <Button onClick={closeModal} data-id="element-1489">Fermer</Button>
                </div> : <form ref={formRef} onSubmit={handleSubmit} className="space-y-6" data-id="element-1490">
                  {/* Formulaire Membre */}
                  {activeModal === 'membre' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1491">
                      <div className="space-y-2" data-id="element-1492">
                        <label className={labelClass} data-id="element-1493">Nom complet *</label>
                        <input type="text" required className={inputClass} name="nom" placeholder="Votre nom" data-id="element-1494" />
                      </div>
                      <div className="space-y-2" data-id="element-1495">
                        <label className={labelClass} data-id="element-1496">Prénom *</label>
                        <input type="text" required className={inputClass} name="prenom" placeholder="Votre prénom" data-id="element-1497" />
                      </div>
                      <div className="space-y-2" data-id="element-1498">
                        <label className={labelClass} data-id="element-1499">Téléphone *</label>
                        <input type="tel" required className={inputClass} name="telephone" placeholder="+229..." data-id="element-1500" />
                      </div>
                      <div className="space-y-2" data-id="element-1501">
                        <label className={labelClass} data-id="element-1502">Email *</label>
                        <input type="email" required className={inputClass} name="email" placeholder="exemple@email.com" data-id="element-1503" />
                      </div>
                      <div className="space-y-2" data-id="element-1504">
                        <label className={labelClass} data-id="element-1505">Ville *</label>
                        <input type="text" required className={inputClass} name="ville" placeholder="Ex: Cotonou" data-id="element-1506" />
                      </div>
                      <div className="space-y-2" data-id="element-1507">
                        <label className={labelClass} data-id="element-1508">Profession</label>
                        <input type="text" className={inputClass} name="profession" placeholder="Votre métier actuel" data-id="element-1509" />
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-1510">
                        <label className={labelClass} data-id="element-1511">Type de membre *</label>
                        <select required className={selectClass} name="typeMembre" data-id="element-1512">
                          <option value="" data-id="element-1513">Sélectionner...</option>
                          <option value="sympathisant" data-id="element-1514">
                            Membre sympathisant (soutien moral)
                          </option>
                          <option value="actif" data-id="element-1515">
                            Membre actif (participation aux activités)
                          </option>
                          <option value="bienfaiteur" data-id="element-1516">
                            Membre bienfaiteur (soutien financier)
                          </option>
                          <option value="honneur" data-id="element-1517">Membre d'honneur</option>
                        </select>
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-1518">
                        <label className={labelClass} data-id="element-1519">
                          Comment souhaitez-vous contribuer ?
                        </label>
                        <select className={selectClass} name="contribution" data-id="element-1520">
                          <option value="" data-id="element-1521">Sélectionner...</option>
                          <option value="benevolat" data-id="element-1522">Bénévolat / Temps</option>
                          <option value="expertise" data-id="element-1523">Partage d'expertise</option>
                          <option value="reseau" data-id="element-1524">Mise en réseau</option>
                          <option value="financier" data-id="element-1525">Soutien financier</option>
                          <option value="autre" data-id="element-1526">Autre</option>
                        </select>
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-1527">
                        <label className={labelClass} data-id="element-1528">
                          Motivations (facultatif)
                        </label>
                        <textarea rows={3} className={inputClass + ' resize-none'} name="motivations" placeholder="Pourquoi souhaitez-vous rejoindre l'ADCC ?" data-id="element-1529"></textarea>
                      </div>
                    </div>}

                  {/* Formulaire Recruter */}
                  {activeModal === 'recruter' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1530">
                      <div className="space-y-2" data-id="element-1531">
                        <label className={labelClass} data-id="element-1532">Nom complet *</label>
                        <input name="nom" type="text" required className={inputClass} placeholder="Votre nom" data-id="element-1533" />
                      </div>
                      <div className="space-y-2" data-id="element-1534">
                        <label className={labelClass} data-id="element-1535">Prénom *</label>
                        <input name="prenom" type="text" required className={inputClass} placeholder="Votre prénom" data-id="element-1536" />
                      </div>
                      <div className="space-y-2" data-id="element-1537">
                        <label className={labelClass} data-id="element-1538">Téléphone *</label>
                        <input name="telephone" type="tel" required className={inputClass} placeholder="+229..." data-id="element-1539" />
                      </div>
                      <div className="space-y-2" data-id="element-1540">
                        <label className={labelClass} data-id="element-1541">Email *</label>
                        <input name="email" type="email" required className={inputClass} placeholder="exemple@email.com" data-id="element-1542" />
                      </div>
                      <div className="space-y-2" data-id="element-1543">
                        <label className={labelClass} data-id="element-1544">Ville *</label>
                        <input name="ville" type="text" required className={inputClass} placeholder="Ex: Cotonou" data-id="element-1545" />
                      </div>
                      <div className="space-y-2" data-id="element-1546">
                        <label className={labelClass} data-id="element-1547">Quartier</label>
                        <input name="quartier" type="text" className={inputClass} placeholder="Ex: Haie Vive" data-id="element-1548" />
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-1549">
                        <label className={labelClass} data-id="element-1550">
                          Type de personnel recherché *
                        </label>
                        <select name="typePersonnel" required className={selectClass} data-id="element-1551">
                          <option value="" data-id="element-1552">Sélectionner...</option>
                          <option value="menage" data-id="element-1553">
                            Personnel d'entretien ménager
                          </option>
                          <option value="cuisine" data-id="element-1554">Cuisinier(ère)</option>
                          <option value="garde" data-id="element-1555">
                            Garde d'enfants / Nounou
                          </option>
                          <option value="gardien" data-id="element-1556">Gardien</option>
                          <option value="chauffeur" data-id="element-1557">Chauffeur</option>
                          <option value="maintenance" data-id="element-1558">
                            Agent de maintenance
                          </option>
                          <option value="polyvalent" data-id="element-1559">
                            Personnel polyvalent
                          </option>
                        </select>
                      </div>
                      <div className="space-y-2" data-id="element-1560">
                        <label className={labelClass} data-id="element-1561">
                          Type de contrat souhaité
                        </label>
                        <select name="typeContrat" className={selectClass} data-id="element-1562">
                          <option value="" data-id="element-1563">Sélectionner...</option>
                          <option value="temps_plein" data-id="element-1564">
                            Temps plein (logé ou non)
                          </option>
                          <option value="temps_partiel" data-id="element-1565">Temps partiel</option>
                          <option value="ponctuel" data-id="element-1566">
                            Intervention ponctuelle
                          </option>
                        </select>
                      </div>
                      <div className="space-y-2" data-id="element-1567">
                        <label className={labelClass} data-id="element-1568">
                          Date de début souhaitée
                        </label>
                        <input name="dateDebut" type="date" className={inputClass} data-id="element-1569" />
                      </div>
                      <div className="space-y-2 md:col-span-2" data-id="element-1570">
                        <label className={labelClass} data-id="element-1571">
                          Description du poste et attentes *
                        </label>
                        <textarea name="description" rows={4} required className={inputClass + ' resize-none'} placeholder="Décrivez les tâches, horaires, conditions..." data-id="element-1572"></textarea>
                      </div>
                    </div>}

                  {activeModal === 'membre' && <div className="bg-blue-50 border border-blue-100 rounded-lg p-4" data-id="element-1573">
                      <p className="text-sm text-gray-700" data-id="element-1574">
                        <strong data-id="element-1575">En devenant membre</strong>, vous rejoignez une
                        communauté engagée pour la dignité et l'insertion
                        professionnelle. Vous serez informé(e) de nos activités
                        et pourrez participer à nos événements.
                      </p>
                    </div>}

                  {activeModal === 'recruter' && <div className="bg-green-50 border border-green-100 rounded-lg p-4" data-id="element-1576">
                      <p className="text-sm text-gray-700" data-id="element-1577">
                        <strong data-id="element-1578">En soumettant cette demande</strong>, vous serez
                        contacté(e) par notre équipe qui vous proposera des
                        profils correspondant à vos critères. Tous nos
                        personnels sont formés et accompagnés dans un cadre
                        éthique.
                      </p>
                    </div>}

                  <div className="pt-4 border-t border-gray-100 flex justify-end gap-4" data-id="element-1579">
                    <Button type="button" variant="ghost" onClick={closeModal} data-id="element-1580">
                      Annuler
                    </Button>
                    <Button type="submit" disabled={isSubmitting} className="min-w-[180px]" data-id="element-1581">
                      {isSubmitting ? <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" data-id="element-1582" />
                          Envoi en cours...
                        </> : 'Soumettre ma demande'}
                    </Button>
                  </div>
                </form>}
            </div>
          </div>
        </div>}
    </Layout>;
}